import { formatNumber } from './helpers';

// Default measurement system
let measurementSystem = 'metric';

/** Function to set the measurement system from user context ('metric' or 'imperial') */
export const setMeasurementSystem = system => (measurementSystem = system);

// Base value factors for length conversions
const METERS_TO_METERS_FACTOR = 1;
const METERS_TO_FEET_FACTOR = 3.2808398950131233595800524934383;

// Base value factors for area conversions
const SQUARE_METERS_TO_SQUARE_METERS_FACTOR = 1;
const SQUARE_METERS_TO_SQUARE_FEET_FACTOR = 10.7639104167097223083335055559;

/**
 * Configuration for measurement system conversions.
 *
 * This object defines the conversion settings for different measurement types (length and area)
 * across different measurement systems (metric and imperial).
 *
 * Each measurement type follows these conversion steps:
 * 1. Input values are always in base metric units (meters or square meters)
 *
 * 2. The value is converted to the target measurement system's base unit using baseValueConvertionFactor
 *    - For metric: meters → meters or square meters → square meters (factor = 1)
 *    - For imperial: meters → feet or square meters → square feet (using conversion factors)
 *
 * 3. If unit scaling is enabled, the base value is compared against threshold values:
 *    - If baseValue ≥ threshold, it's divided by the corresponding divisor and uses the new unit
 *    - If baseValue < threshold, it keeps the base unit
 *
 * For example, with imperial length:
 * - 100 meters → 328.08 ft (below 5280ft threshold, stays as feet)
 * - 2000 meters → 6561.68 ft → 1.24 mi (above 5280ft threshold, converts to miles)
 *
 * @type {Object}
 * @property {Object} length - Length measurement configurations
 * @property {Object} length.metric - Metric length configuration (m → km)
 * @property {Object} length.imperial - Imperial length configuration (ft → mi)
 *
 * @property {Object} area - Area measurement configurations
 * @property {Object} area.metric - Metric area configuration (m² → km²)
 * @property {Object} area.imperial - Imperial area configuration (ft² → ac)
 */
const MEASUREMENT_SYSTEMS = Object.freeze({
	length: {
		metric: {
			baseUnit: 'm',
			baseValueConvertionFactor: METERS_TO_METERS_FACTOR,
			conversions: [{ threshold: 1000, divisor: 1000, unit: 'km' }],
		},

		imperial: {
			baseUnit: 'ft',
			baseValueConvertionFactor: METERS_TO_FEET_FACTOR,
			conversions: [{ threshold: 5280, divisor: 5280, unit: 'mi' }],
		},
	},

	area: {
		metric: {
			baseUnit: 'm²',
			baseValueConvertionFactor: SQUARE_METERS_TO_SQUARE_METERS_FACTOR,
			conversions: [
				{ threshold: 10_000, divisor: 10_000, unit: 'ha' },
				{ threshold: 1_000_000, divisor: 1_000_000, unit: 'km²' },
			],
		},

		imperial: {
			baseUnit: 'ft²',
			baseValueConvertionFactor: SQUARE_METERS_TO_SQUARE_FEET_FACTOR,
			conversions: [{ threshold: 43_560, divisor: 43_560, unit: 'ac' }],
		},
	},
});

/**
 * Applies appropriate value and unit scaling based on the provided conversions.
 * @param {Array} conversions - Array of conversion options containing threshold, divisor, and unit (e.g., [{ threshold: 1000, divisor: 1000, unit: 'km' }])
 * @param {number} baseValue - The base value to format (eg., 1234)
 * @param {string} baseUnit - The base unit (e.g., 'm', 'ft', 'm²', 'ft²')
 * @returns {Array} [resultValue, resultUnit] (e.g., [1.234, 'km'])
 */
const applyConversions = (conversions, baseValue, baseUnit) => {
	// Set default values (If thresholds are not met, it will return the base value and unit)
	let scaledValue = baseValue;
	let convertedUnit = baseUnit;

	// Iterate through the conversions to find the appropriate scaling and unit
	for (const { threshold, divisor, unit: newUnit } of conversions) {
		if (Math.abs(baseValue) < threshold) break; // Exit loop if value is smaller than threshold
		scaledValue = baseValue / divisor;
		convertedUnit = newUnit;
	}

	return [scaledValue, convertedUnit];
};

/**
 * Formats measurement values with appropriate units based on measurement type and system.
 * Applies unit scaling when appropriate and formats the number with comma separators.
 *
 * @param {string} measurementType - Type of measurement ('length' or 'area')
 * @param {number} value - The measurement value to format (assumed to be in base metric units: meters or square meters)
 * @param {number} decimalPlaces - Number of decimal places to display in the result
 * @param {boolean} allowUnitScaling - Whether to scale units based on value magnitude
 *                                             (e.g., m to km or ft to mi for large values)
 * @returns {Array} Array containing [formattedNumberString, unitSymbol]
 * @throws {Error} If an invalid measurement type is provided
 */
const formatMeasurement = (measurementType, value, decimalPlaces, allowUnitScaling) => {
	// Length or Area.
	const measurementTypeConfig = MEASUREMENT_SYSTEMS[measurementType] ?? null;
	if (!measurementTypeConfig) throw new Error('Invalid measurement type');

	// Metric or Imperial config.
	const measurementSystemConfig =
		measurementTypeConfig[measurementSystem] ?? measurementTypeConfig.metric;

	// Destructure the config for easier access
	const { baseUnit, baseValueConvertionFactor, conversions } = measurementSystemConfig;

	// Convert the value to the base unit according to the measurement system (meters for metric, feet for imperial)
	const baseValue = value * baseValueConvertionFactor;

	// If allowUnitScaling is true, it will check conversion thresholds and apply unit scaling if threshold is met
	// Else, it will return the base value and unit
	// (e.g., [1000, 'm'] => [1, 'km'])
	const [resultValue, resultUnit] = allowUnitScaling
		? applyConversions(conversions, baseValue, baseUnit)
		: [baseValue, baseUnit];

	// Returns the formatted number and correct unit according to the measurement system
	return [formatNumber(resultValue, decimalPlaces), resultUnit];
};

/**
 * Formats a length measurement with appropriate units based on the current measurement system.
 * Uses the globally set measurement system (metric or imperial) and applies unit scaling when specified.
 *
 * @param {number} meters - The length value in meters to format
 * @param {number} [decimalPlaces=1] - Number of decimal places to display in the formatted result
 * @param {boolean} [allowUnitScaling=true] - Whether to automatically scale units based on value magnitude
 *                                            (e.g., m to km or ft to mi for large values)
 * @returns {Array} Array containing [formattedNumberString, unitSymbol]
 *
 * @example
 * // With measurement system set to 'metric'
 * formatLength(1500); // Returns ["1,500.00", "m"]
 * formatLength(1500, 1, true); // Returns ["1.5", "km"]
 *
 * @example
 * // With measurement system set to 'imperial'
 * formatLength(1500); // Returns ["4,921.26", "ft"]
 * formatLength(1500, 2, true); // Returns ["0.93", "mi"]
 */
export const formatLength = (meters, decimalPlaces = 1, allowUnitScaling = true) =>
	formatMeasurement('length', meters, decimalPlaces, allowUnitScaling);

/**
 * Formats an area measurement with appropriate units based on the current measurement system.
 * Uses the globally set measurement system (metric or imperial) and applies unit scaling when specified.
 *
 * @param {number} squaredMeters - The area value in square meters to format
 * @param {number} [decimalPlaces=1] - Number of decimal places to display in the formatted result
 * @param {boolean} [allowUnitScaling=true] - Whether to automatically scale units based on value magnitude
 *                                            (e.g., m² to km² or ft² to ac for large values)
 * @returns {Array} Array containing [formattedNumberString, unitSymbol]
 *
 * @example
 * // With measurement system set to 'metric'
 * formatArea(5000); // Returns ["5,000.00", "m²"]
 * formatArea(2000000, 1, true); // Returns ["2.0", "km²"]
 *
 * @example
 * // With measurement system set to 'imperial'
 * formatArea(5000); // Returns ["53,819.55", "ft²"]
 * formatArea(10000, 3, true); // Returns ["2.471", "ac"]
 */
export const formatArea = (squaredMeters, decimalPlaces = 1, allowUnitScaling = true) =>
	formatMeasurement('area', squaredMeters, decimalPlaces, allowUnitScaling);

/**
 * Converts a measurement in meters to feet.
 * @param {number} meters - The distance in meters to convert.
 * @returns {number} The distance converted to feet.
 */
export const meterToFt = meters => meters * METERS_TO_FEET_FACTOR;

/**
 * Converts an area measurement from square meters to square feet.
 * @param {number} squareMeters - The area in square meters to convert.
 * @returns {number} The converted area in square feet.
 */
export const squareMeterToSquareFt = squareMeters =>
	squareMeters * SQUARE_METERS_TO_SQUARE_FEET_FACTOR;

/**
 * Converts square kilometers to square feet.
 * @param {number} squareKilometers - The area in square kilometers to convert.
 * @returns {number} The converted area in square feet.
 */
export const squareKilometerToSquareFt = squareKilometers =>
	squareKilometers * 1_000_000 * SQUARE_METERS_TO_SQUARE_FEET_FACTOR;

/**
 * Converts feet to meters.
 * @param {number} feet - The distance in feet to convert.
 * @returns {number} The equivalent distance in meters.
 */
export const ftToMeter = feet => feet / METERS_TO_FEET_FACTOR;

/**
 * Converts an area measurement from square feet to square meters.
 * @param {number} squareFeet - The area in square feet to be converted.
 * @returns {number} The converted area in square meters.
 */
export const squareFtToSquareMeter = squareFeet => squareFeet / SQUARE_METERS_TO_SQUARE_FEET_FACTOR;

/**
 * Converts acres to square meters.
 * @param {number} acres - The area in acres to convert.
 * @returns {number} The converted area in square meters.
 */
export const acresToSquareMeter = acres => (acres * 43_560) / SQUARE_METERS_TO_SQUARE_FEET_FACTOR;
